import { useEffect, useState, useMemo } from "react";
import { RefreshCcw, Search, Skull, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const SIGNALS = ["TERM", "HUP", "INT", "STOP", "CONT", "KILL"];
const SORTS = [
  { k: "cpu", label: "CPU" },
  { k: "memory", label: "RAM" },
  { k: "pid", label: "PID" },
  { k: "name", label: "Name" },
];

export default function Processes() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("cpu");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/processes", {
        params: { search, sort_by: sort, limit: 500 },
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [sort]);

  const sendSignal = async (pid, signal) => {
    setNotice("");
    try {
      await api.post(`/processes/${pid}/signal`, { signal });
      setNotice(`Signal ${signal} → PID ${pid} gesendet.`);
      setTimeout(load, 300);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const killProcess = async (pid) => {
    if (!window.confirm(`PID ${pid} KILLEN (SIGKILL)?`)) return;
    try {
      await api.delete(`/processes/${pid}`);
      setNotice(`PID ${pid} getötet.`);
      setTimeout(load, 300);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div data-testid="processes-page">
      <PageHeader title="Prozesse" subtitle="// processes" testId="processes-header">
        <button
          onClick={load}
          data-testid="processes-refresh-button"
          className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white transition-colors"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
          Aktualisieren
        </button>
      </PageHeader>

      <div className="px-8 py-4 flex items-center gap-3 flex-wrap grid-border-b" style={{ borderBottom: "1px solid #1f1f22" }}>
        <div className="flex items-center gap-2 bg-black border border-[#1f1f22] px-2 py-1.5 flex-1 max-w-sm focus-within:border-[#22d3ee]">
          <Search className="w-3.5 h-3.5 text-[#8b8d98]" strokeWidth={1.5} />
          <input
            data-testid="processes-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Suche: name, cmdline, pid…"
            className="bg-transparent outline-none w-full font-mono text-xs text-white placeholder-[#3f3f46]"
          />
        </div>

        <div className="flex items-center gap-1">
          <span className="overline mr-2">sort</span>
          {SORTS.map((s) => (
            <button
              key={s.k}
              onClick={() => setSort(s.k)}
              data-testid={`processes-sort-${s.k}`}
              className={[
                "px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] border",
                sort === s.k
                  ? "bg-[#22d3ee]/10 border-[#22d3ee] text-[#22d3ee]"
                  : "border-[#1f1f22] text-[#8b8d98] hover:text-white",
              ].join(" ")}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="overline ml-auto">total: {total}</div>
      </div>

      {err ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#dc2626] border border-[#dc2626]/40 bg-[#dc2626]/5 px-3 py-2">
          {err}
        </div>
      ) : null}
      {notice ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#10b981] border border-[#10b981]/40 bg-[#10b981]/5 px-3 py-2">
          {notice}
        </div>
      ) : null}

      <div className="mx-8 my-4 bg-[#0a0a0a]" style={{ border: "1px solid #1f1f22" }}>
        <div className="grid grid-cols-12 gap-4 px-4 py-2.5 grid-border-b overline" style={{ borderBottom: "1px solid #1f1f22" }}>
          <div className="col-span-1">PID</div>
          <div className="col-span-2">Name</div>
          <div className="col-span-1">User</div>
          <div className="col-span-1">CPU%</div>
          <div className="col-span-1">RAM</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-3">Cmd</div>
          <div className="col-span-2 text-right">Aktionen</div>
        </div>

        <div className="divide-y divide-[#1f1f22]">
          {filtered.slice(0, 300).map((p) => (
            <div
              key={p.pid}
              data-testid={`process-row-${p.pid}`}
              className="grid grid-cols-12 gap-4 px-4 py-2 items-center font-mono text-[11px] row-hover"
            >
              <div className="col-span-1 text-[#22d3ee]">{p.pid}</div>
              <div className="col-span-2 text-white truncate">{p.name}</div>
              <div className="col-span-1 text-[#8b8d98] truncate">{p.username}</div>
              <div className={`col-span-1 ${p.cpu_percent > 50 ? "text-[#dc2626]" : "text-white"}`}>
                {p.cpu_percent.toFixed(1)}
              </div>
              <div className="col-span-1 text-white">{p.memory_mb.toFixed(0)}M</div>
              <div className="col-span-1 text-[#8b8d98]">{p.status}</div>
              <div className="col-span-3 text-[#8b8d98] truncate" title={p.cmdline}>
                {p.cmdline || "—"}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                {SIGNALS.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendSignal(p.pid, s)}
                    data-testid={`process-signal-${s}-${p.pid}`}
                    className={[
                      "px-1.5 py-0.5 text-[10px] font-mono border",
                      s === "KILL"
                        ? "border-[#dc2626]/60 text-[#dc2626] hover:bg-[#dc2626]/10"
                        : "border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white",
                    ].join(" ")}
                    title={`SIG${s}`}
                  >
                    {s}
                  </button>
                ))}
                <button
                  onClick={() => killProcess(p.pid)}
                  data-testid={`process-kill-button-${p.pid}`}
                  className="ml-1 p-1 border border-[#dc2626]/60 text-[#dc2626] hover:bg-[#dc2626] hover:text-white"
                  title="Kill (force)"
                >
                  <Skull className="w-3 h-3" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
          {!filtered.length && (
            <div className="px-4 py-12 text-center text-xs font-mono text-[#8b8d98]">
              <Ban className="w-4 h-4 inline mr-2 opacity-60" strokeWidth={1.5} />
              Keine Prozesse gefunden.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
